import { useState } from "react";
import { motion } from "framer-motion";
import { ShoppingCart, Plus } from "lucide-react";
import { ImageLightbox } from "@/components/ImageLightbox";

/**
 * MenuItem - row in a list. Optional `onAdd` shows the "Pedir" button.
 */
export const MenuItem = ({ name, description, price, index = 0, onAdd }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="flex items-start justify-between gap-4 py-4 border-b border-dashed border-[#1A1A1A]/15 last:border-b-0 group"
    >
      <div className="flex-1 min-w-0">
        <h4 className="font-['Playfair_Display'] text-base md:text-lg font-semibold text-[#1A1A1A] group-hover:text-[#556B2F] transition-colors duration-300">
          {name}
        </h4>
        {description && (
          <p className="font-['DM_Sans'] text-sm text-[#4A4A4A] mt-1 leading-relaxed">
            {description}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        {price !== undefined && (
          <span className="font-['DM_Sans'] text-lg font-bold text-[#C41E3A]">${price}</span>
        )}
        {onAdd && (
          <button
            data-testid={`add-btn-${name.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={onAdd}
            className="inline-flex items-center gap-1 bg-[#556B2F] hover:bg-[#3F4F22] text-white text-xs font-['DM_Sans'] font-semibold px-3 py-1.5 rounded-full transition-colors"
          >
            <Plus className="w-3 h-3" /> Pedir
          </button>
        )}
      </div>
    </motion.div>
  );
};

export const BentoCard = ({ name, description, price, image, label, onAdd }) => {
  return (
    <div className="relative my-4">
      <ImageLightbox src={image} alt={name}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6 }}
          className="relative h-[240px] md:h-[300px] rounded-2xl overflow-hidden group cursor-pointer"
        >
          <img
            src={image}
            alt={name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 pr-32 md:pr-40">
            {label && (
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C41E3A] font-['DM_Sans']">
                {label}
              </span>
            )}
            <h3 className="text-white font-['Playfair_Display'] text-xl md:text-2xl font-bold mt-1">
              {name}
            </h3>
            {description && (
              <p className="text-white/75 text-sm font-['DM_Sans'] mt-1 line-clamp-2">
                {description}
              </p>
            )}
            {price !== undefined && (
              <p className="text-white font-['DM_Sans'] font-bold text-lg mt-2">${price}</p>
            )}
          </div>
        </motion.div>
      </ImageLightbox>
      {onAdd && (
        <button
          data-testid={`add-btn-bento-${name.replace(/\s+/g, "-").toLowerCase()}`}
          onClick={(e) => {
            e.stopPropagation();
            onAdd();
          }}
          className="absolute bottom-4 right-4 md:bottom-5 md:right-5 z-10 inline-flex items-center gap-1.5 bg-[#C41E3A] hover:bg-[#9A172D] text-white text-sm font-['DM_Sans'] font-bold px-4 py-2 rounded-full shadow-lg transition-colors"
        >
          <ShoppingCart className="w-4 h-4" /> Pedir
        </button>
      )}
    </div>
  );
};

export const PriceTable = ({ sizes, title }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-white/50 rounded-xl p-4 md:p-6 mb-6 border border-[#1A1A1A]/8"
    >
      {title && (
        <h4 className="font-['DM_Sans'] text-xs uppercase tracking-[0.15em] font-bold text-[#556B2F] mb-3">
          {title}
        </h4>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {sizes.map((s) => (
          <div key={s.size} className="text-center bg-white/70 rounded-lg p-3 border border-[#1A1A1A]/5">
            <span className="block font-['DM_Sans'] text-xs text-[#4A4A4A] font-medium">{s.size}</span>
            {s.slices && <span className="block font-['DM_Sans'] text-[10px] text-[#4A4A4A]/70">{s.slices}</span>}
            <span className="block font-['DM_Sans'] text-lg font-bold text-[#C41E3A] mt-1">${s.price}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

/* Big section CTA to start ordering a custom pizza (combinadas/especiales) */
export const OrderPizzaCTA = ({ label, onClick }) => (
  <motion.button
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    onClick={onClick}
    data-testid={`order-cta-${label.replace(/\s+/g, "-").toLowerCase()}`}
    className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#C41E3A] hover:bg-[#9A172D] text-white font-['DM_Sans'] font-bold px-6 py-3 rounded-full transition-colors shadow-md"
  >
    <ShoppingCart className="w-4 h-4" /> {label}
  </motion.button>
);
